import { ChatExchangeModel, ChatThreadModel, ChatTurn, ResponseContext, SimpleChatRequestDto } from "../../../@api";

import { translationKeys } from "../../models/app-translation-keys";
import { IMiddelware } from "../../models/interfaces/middelware.interface";
import { UserStore } from "../../stores";
import { createResponse, getRequestBodyContent, getResponseBodyContent } from "./middlewareUtils";

export class ChatErrorResponseHandlingMiddleware implements IMiddelware {
  public readonly unhandledHttpStatusCodes = [422];

  constructor(private readonly userStore: UserStore) {}

  public async post(context: ResponseContext): Promise<Response | void> {
    if (!this.unhandledHttpStatusCodes.includes(context.response.status)) {
      this.resetError();
      return;
    }

    this.setError(context);

    const bodyContent = await getResponseBodyContent(context);

    const simpleChatRequest = await getRequestBodyContent<SimpleChatRequestDto>(context);
    if (!simpleChatRequest) {
      return;
    }

    let answer: string = "";
    let error: string = "";
    if (context.response.status === 422) {
      answer =
        this.translate(translationKeys.thread.error.contentFilter) +
        "\n" +
        this.translate(translationKeys.thread.error.contentFilterDescription);
    } else {
      answer = "An error happened:\n" + JSON.stringify(bodyContent);
      error = JSON.stringify(bodyContent);
    }

    const exchanges = this.mapExchangesFromHistory(simpleChatRequest);

    this.addOrUpdateLastExchange(exchanges, {
      id: `error-${exchanges.length + 1}`,
      threadId: `error-${exchanges.length + 1}`,
      answer: answer,
      error: error,
      prompt: simpleChatRequest.history?.[simpleChatRequest.history.length - 1]?.fromUser ?? "",
      askedOn: new Date(),
    });

    if (!this.userStore.userInfo?.azureId) {
      throw new Error("User ID is not available for creating a new thread.");
    }

    const newBody: ChatThreadModel = {
      id: "00000000-0000-0000-0000-000000000000",
      userId: this.userStore.userInfo.azureId,
      visibleToUser: true,
      threadSummary: "",
      exchanges,
      lastActivity: new Date(),
    };

    this.resetError();

    return createResponse(newBody, context, { status: 200 });
  }

  public resetError() {}

  public async setError(context: ResponseContext) {}

  private translate(key: string): string {
    const translatedValue = this.userStore.i18nInstance?.t(key);
    return translatedValue ? translatedValue.toString() : key;
  }

  private mapExchangesFromHistory(simpleChatRequest: SimpleChatRequestDto | undefined): ChatExchangeModel[] {
    return (
      simpleChatRequest?.history?.map(
        (ct: ChatTurn): ChatExchangeModel => ({
          prompt: ct.fromUser,
          answer: ct.fromBot,
          threadId: simpleChatRequest.threadId!,
          id: "",
          askedOn: new Date(),
        }),
      ) ?? []
    );
  }

  private addOrUpdateLastExchange(exchanges: ChatExchangeModel[], exchange: ChatExchangeModel) {
    if (exchanges?.length > 0) {
      const lastIndex = exchanges.length - 1;
      exchanges[lastIndex] = { ...exchanges[lastIndex], ...exchange };
    } else {
      exchanges.push(exchange);
    }
  }
}
